export interface TrackedQuery {
  isEnabled: boolean;
  isFetching: boolean;
  isFetchedAfterMount: boolean;
  isStale: boolean;
  data: unknown;
}

// Immutable on purpose: the React Compiler may memoize a mutable object across the hook calls
export class PrefetchQueue {
  private constructor(
    private readonly open: boolean,
    private readonly limit: number,
    private readonly unfinished: number,
  ) {}

  static start(open: boolean, limit: number): PrefetchQueue {
    return new PrefetchQueue(open, limit, 0);
  }

  get enabled(): boolean {
    return this.open && this.unfinished < this.limit;
  }

  options<Options extends object>(
    options: Options,
  ): Options & { enabled: boolean } {
    return { ...options, enabled: this.enabled };
  }

  after(query: TrackedQuery): PrefetchQueue {
    return new PrefetchQueue(
      this.open,
      this.limit,
      hasFinished(query) ? this.unfinished : this.unfinished + 1,
    );
  }
}

// A disabled query reports isStale as false, so fresh data only counts once the query may fetch
function hasFinished(query: TrackedQuery): boolean {
  if (query.isFetching) {
    return false;
  }
  if (query.isFetchedAfterMount) {
    return true;
  }
  return query.isEnabled && query.data !== undefined && !query.isStale;
}
